// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import ProductLine, { ProductLinePresentationProps } from "@store/Library/Components/ProductLine/ProductLine";
import {
    productLineStatus,
    ProductLineStatusType,
} from "@store/Library/Components/ProductLine/ProductLineStatusOverlay";
import { orderV2Props } from "@store/Themes/common/props/orderV2";
import React, { FC, useContext } from "react";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import getOrderChangeRequestProductLineStatus from "@store/Framework/Common/Utilities/OrderChangeRequest/getOrderChangeRequestProductLineStatus";
import { css } from "styled-components";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import getColor from "@insite/mobius/utilities/getColor";
import OrderChangeRequestLineInformation from "@store/Library/Components/OrderChangeRequest/OrderChangeRequestLineInformation";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";

const statusConfig: { status: ProductLineStatusType | undefined; label?: () => string }[] = [
    { status: "modified", label: () => translate("Modified") },
    { status: "added", label: () => translate("Added") },
    { status: undefined },
    { status: "canceled", label: () => translate("Canceled") },
];

const getStatusOrder = (status: ProductLineStatusType | undefined) => {
    return statusConfig.findIndex(x => x.status === status);
};

export interface OrderChangeRequestReviewProductListProps extends WidgetProps {}

export interface OrderChangeRequestReviewProductListStyles {
    productLine?: ProductLinePresentationProps;
    statusCount?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const OrderChangeRequestReviewProductListStyles: OrderChangeRequestReviewProductListStyles = {
    wrapper: orderV2Props.sectionWrapper,
    title: mergeProps<TypographyPresentationProps>(orderV2Props.sectionTitle, {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(6)} 0;
        `,
    }),
    productLine: orderV2Props.productLine,
    statusCount: {
        css: css`
            ${inlineSeparatorStyles({ color: "text", spacing: pxToRem(21) })}

            &.added {
                color: ${getColor(productLineStatus.added.backgroundColor)};
            }

            &.canceled {
                color: ${getColor(productLineStatus.canceled.backgroundColor)};
            }

            &.modified {
                color: ${getColor(productLineStatus.modified.backgroundColor)};
            }
        `,
    },
};

const styles = OrderChangeRequestReviewProductListStyles;

const OrderChangeRequestReviewProductList: FC<OrderChangeRequestReviewProductListProps> = () => {
    const { value: order } = useContext(OrderStateContext);
    const orderChangeRequest = useAppSelector(state => state.greif.pages.orderChange.changeRequestOrder);

    if (!order) {
        return null;
    }

    const productLines = [
        ...(order.orderLines ?? []).map(orderLine => {
            const orderChangeRequestLine = orderChangeRequest?.orderLines?.find(x => x.orderLineId === orderLine.id);
            const status = getOrderChangeRequestProductLineStatus(orderChangeRequestLine);

            return {
                status,
                element: (
                    <ProductLine
                        {...styles.productLine}
                        additionalAlternativeContent={
                            orderChangeRequestLine ? (
                                <OrderChangeRequestLineInformation lineState={orderChangeRequestLine} status={status} />
                            ) : undefined
                        }
                        key={orderLine.id}
                        productLine={orderLine}
                        showPrice
                        showSubtotal
                        showLineNumber
                        showFulfillment
                        status={status}
                    />
                ),
            };
        }),
        ...(orderChangeRequest?.newLines ?? []).map(newLine => {
            const status: ProductLineStatusType = "added";

            return {
                status,
                element: (
                    <ProductLine
                        {...styles.productLine}
                        additionalAlternativeContent={
                            <OrderChangeRequestLineInformation lineState={newLine} status={status} />
                        }
                        key={newLine.product.id}
                        productLine={{
                            ...newLine.product,
                            pricing: newLine.productInfo.pricing ?? null,
                            qtyOrdered: newLine.newQuantity === "" ? null : Number(newLine.newQuantity),
                        }}
                        productInfo={newLine.productInfo}
                        showLineNumber
                        showFulfillment
                        showQuantity={false}
                        status={status}
                    />
                ),
            };
        }),
    ].sort((a, b) => getStatusOrder(a.status) - getStatusOrder(b.status));

    const statusCounts = statusConfig.map(() => 0);
    productLines.forEach(x => {
        statusCounts[getStatusOrder(x.status)] += 1;
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title} data-test-selector="orderChangeRequestReviewProductList_statusHeader">
                <Typography {...styles.statusCount} data-test-selector="orderChangeRequestReviewProductList_totalCount">
                    {translate("{0} Items total", productLines.filter(x => x.status !== "canceled").length.toString())}
                </Typography>
                {statusConfig.map(({ status, label }, index) => {
                    const count = statusCounts[index];

                    if (!label || !count) {
                        return null;
                    }

                    return (
                        <Typography
                            {...styles.statusCount}
                            className={status}
                            key={status}
                            data-test-selector={`orderChangeRequestReviewProductList_${status}Count`}
                        >
                            {count}&nbsp;{label()}
                        </Typography>
                    );
                })}
            </Typography>
            {productLines.map(x => x.element)}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderChangeRequestReviewProductList,
    definition: {
        group: constants.widgetGroup.orderChangeRequestReview,
        displayName: "Order Change Request Review Product List",
        allowedContexts: [constants.pageType.orderChangeRequestReview],
    },
};

export default widgetModule;
